import React, { useEffect, useState } from "react";
import { Modal, ModalDialog, Button, Input, Checkbox, Chip, CircularProgress } from "@mui/joy";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postFetch } from "../../../../../../fetch/fetch";
import styles from "./staffmanage.module.css";
import { CONTRACT_VAR_KEYS, varKeyLabel } from "./contractvarkeys";
import { API, toastOptions, errorText } from "./rosterutils";

//계약서 일괄 발송 — 체크한 직무들을 미리보기(POST /send/preview) → 계약기간·값 편집 → 순차 발송(POST /send) → 결과
//다이얼로그에서 고친 값(계약기간 제외)은 그 직무의 개인 오버라이드로 저장됨 (개인 변수 팝업과 같은 저장소)
//발송은 SEND_CHUNK 건씩 나눠 순서대로 요청 — postFetch 30초 타임아웃 때문 (10/22 87건 한 번에 보내니 41초 걸려 화면은 오류, 서버는 전부 발송)
//한 묶음이 응답을 못 받으면 거기서 멈춤: 그 묶음은 "확인 필요"(서버에선 발송됐을 수 있음 — 자동 재시도 금지, 중복 발송 위험), 뒤 묶음은 "보내지 않음"
const SEND_CHUNK = 10;

interface SendRow {
    positionId: number;
    userId: number;
    name: string;
    branchLabel: string;
    partName: string;
    templateName: string;
    ok: boolean;
    reason: string | null;
    code: string | null;
    isFirst: boolean;
    videoConsentStatus: string;
    roundNo: number | null;
    period: { start: string; end: string };
    original: { start: string; end: string; values: { [key: string]: string } };
    values: { [key: string]: string };
    send: boolean;
    expanded: boolean;
}

interface SendResult {
    positionId: number;
    ok: boolean;
    documentId?: string;
    documentName?: string;
    videoConsent?: { ok: boolean; documentId?: string; error?: string };
    error?: string;
    unknown?: boolean;     //응답을 못 받은 묶음 — 실제 발송 여부는 명부에서 확인
    skipped?: boolean;     //앞 묶음이 멈춰서 요청하지 않음
}

//인라인 열에 이미 있는 키는 펼치기 패널에서 제외
const INLINE_KEYS = ["common.hourlyWage", "part.weeklyHours", "dynamic.period"];

const SendContractModal: React.FC<any> = (props) => {

    const [rows, setRows] = useState<SendRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [today, setToday] = useState("");
    const [senderName, setSenderName] = useState("");
    const [editableKeys, setEditableKeys] = useState<string[]>([]);
    const [readOnlyKeys, setReadOnlyKeys] = useState<string[]>([]);
    const [withVideoConsent, setWithVideoConsent] = useState(true);
    const [sending, setSending] = useState(false);
    const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
    const [results, setResults] = useState<SendResult[] | null>(null);

    useEffect(() => {
        if (!props.open) {
            return;
        }
        setRows([]);
        setResults(null);
        setWithVideoConsent(true);
        setSending(false);
        setProgress(null);
        loadPreview();
    }, [props.open]);

    const loadPreview = async () => {
        const positionIds: number[] = props.positionIds || [];
        if (positionIds.length === 0) {
            return;
        }
        try {
            setLoading(true);
            const result: any = await postFetch(`${API}/send/preview`, { positionIds });
            if (result.message !== "success") {
                toast.error(errorText(result, "미리보기를 불러오지 못했습니다"), toastOptions);
                return;
            }
            setToday(result.today || "");
            setSenderName(result.senderName || "");
            setEditableKeys(result.editableKeys || []);
            setReadOnlyKeys(result.readOnlyKeys || []);
            setRows((result.items || []).map((item: any): SendRow => {
                const start = item.period ? item.period.start : "";
                const end = item.period ? item.period.end : "";
                const values = item.values || {};
                return {
                    positionId: item.positionId,
                    userId: item.userId,
                    name: item.name || "",
                    branchLabel: item.branchLabel || "",
                    partName: item.partName || "",
                    templateName: item.templateName || "",
                    ok: !!(item.check && item.check.ok),
                    reason: item.check ? item.check.reason || item.periodError || null : null,
                    code: item.check ? item.check.code || null : null,
                    isFirst: !!item.isFirst,
                    videoConsentStatus: item.videoConsentStatus || "none",
                    roundNo: item.period ? item.period.roundNo : item.roundNo || null,
                    period: { start, end },
                    original: { start, end, values: { ...values } },
                    values: { ...values },
                    send: !!(item.check && item.check.ok),
                    expanded: false
                };
            }));
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setLoading(false);
        }
    };

    const updateRow = (positionId: number, patch: Partial<SendRow>) => {
        setRows((prev) => prev.map((r) => (r.positionId === positionId ? { ...r, ...patch } : r)));
    };

    const updateValue = (positionId: number, key: string, v: string) => {
        setRows((prev) => prev.map((r) => (r.positionId === positionId ? { ...r, values: { ...r.values, [key]: v } } : r)));
    };

    const isEdited = (r: SendRow, key: string) => r.values[key] !== r.original.values[key];
    const isPeriodEdited = (r: SendRow) => r.period.start !== r.original.start || r.period.end !== r.original.end;

    const targets = rows.filter((r) => r.send && r.ok);

    const send = async () => {
        if (targets.length === 0) return;
        for (const t of targets) {
            if (!t.period.start || !t.period.end) {
                toast.error(`${t.name}의 계약기간을 입력하세요`, toastOptions);
                return;
            }
            if (t.period.end < t.period.start) {
                toast.error(`${t.name}의 계약 종료일이 시작일보다 앞섭니다`, toastOptions);
                return;
            }
        }
        if (!window.confirm(`${targets.length}건의 근로계약서를 유캔싸인으로 발송할까요? (직원에게 카카오 알림톡이 갑니다)`)) return;
        const items = targets.map((r) => {
            //바뀐 오버라이드 가능 키만 보냄
            const overrides: any = {};
            editableKeys.forEach((k) => { if (isEdited(r, k)) overrides[k] = r.values[k]; });
            return {
                positionId: r.positionId,
                periodStart: isPeriodEdited(r) ? r.period.start : undefined,
                periodEnd: isPeriodEdited(r) ? r.period.end : undefined,
                overrides
            };
        });
        const all: SendResult[] = [];
        let stopped = false;
        try {
            setSending(true);
            setProgress({ done: 0, total: items.length });
            for (let i = 0; i < items.length; i += SEND_CHUNK) {
                const chunk = items.slice(i, i + SEND_CHUNK);
                if (stopped) {
                    chunk.forEach((it) => all.push({ positionId: it.positionId, ok: false, skipped: true, error: "앞 묶음 오류로 보내지 않음 — 명부에서 확인 후 다시 발송" }));
                    continue;
                }
                let result: any = null;
                try {
                    result = await postFetch(`${API}/send`, { withVideoConsent, items: chunk });
                } catch (e) {
                    console.log(e);
                }
                if (result && result.message === "success") {
                    const got: SendResult[] = result.results || [];
                    chunk.forEach((it) => {
                        const one = got.find((g) => g.positionId === it.positionId);
                        all.push(one || { positionId: it.positionId, ok: false, unknown: true, error: "결과 없음 — 명부에서 발송 여부 확인" });
                    });
                } else {
                    //응답 못 받음(타임아웃·네트워크) 또는 서버 오류 — 이 묶음은 서버에서 일부 발송됐을 수 있음
                    stopped = true;
                    const why = result ? errorText(result, "발송 오류") : "응답을 받지 못함";
                    chunk.forEach((it) => all.push({ positionId: it.positionId, ok: false, unknown: true, error: `${why} — 발송됐을 수 있으니 명부에서 확인 (바로 다시 보내지 마세요)` }));
                    toast.error("발송 중 오류로 멈췄습니다. 결과를 확인하세요", toastOptions);
                }
                setProgress({ done: Math.min(i + SEND_CHUNK, items.length), total: items.length });
            }
            setResults(all);
        } finally {
            setSending(false);
            setProgress(null);
        }
    };

    const renderStatus = (r: SendRow) => {
        if (!r.ok) {
            return (
                <span>
                    <Chip size="sm" variant="soft" color={r.code === "noTemplate" ? "neutral" : "danger"}>{r.code === "noTemplate" ? "탬플릿 미설정" : "차단"}</Chip>
                    <span className={styles.subText} style={{ marginLeft: "6px" }}>{r.reason}</span>
                </span>
            );
        }
        if (r.isFirst) {
            return (
                <span>
                    <Chip size="sm" variant="soft" color="primary">첫 계약</Chip>
                    {withVideoConsent && r.videoConsentStatus === "none" && <span className={styles.subText} style={{ marginLeft: "6px" }}>+ 영상동의서</span>}
                </span>
            );
        }
        return (
            <span>
                <Chip size="sm" variant="soft" color="success">준비됨</Chip>
                <span className={styles.subText} style={{ marginLeft: "6px" }}>{r.roundNo}회차</span>
            </span>
        );
    };

    const inputSx = (edited: boolean) => ({ backgroundColor: edited ? "#fff8e1" : undefined });

    const cell = (w: number | string, children: any, extra: any = {}) => (
        <div style={{ width: typeof w === "number" ? `${w}px` : w, flexShrink: 0, ...extra }}>{children}</div>
    );

    return (
        <Modal open={props.open} onClose={() => { if (!sending) props.onClose(results !== null); }}>
            <ModalDialog sx={{ width: "1560px", maxWidth: "96vw", maxHeight: "90vh", overflow: "auto" }}>

                <div className={styles.modalTitle}>
                    계약서 일괄 발송
                    <span className={styles.subText} style={{ marginLeft: "10px", fontFamily: "Apple_R" }}>
                        선택 {rows.length}건 · 발송자 {senderName} · 계약일자 = 오늘({today})
                    </span>
                </div>

                {results === null &&
                    <>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                            <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
                                <Checkbox size="sm" label="첫 계약인 사람은 영상정보 동의서도 함께 발송 (미발송인 사람만)" checked={withVideoConsent} onChange={(e) => setWithVideoConsent(e.target.checked)} />
                                <span className={styles.subText}>카카오 알림톡으로 발송 (직원 휴대폰 번호)</span>
                            </div>
                            <span className={styles.subText}>계약기간은 자동 계산됨 — 직접 고치면 노란색으로 표시</span>
                        </div>

                        {loading
                            ?
                            <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}><CircularProgress /></div>
                            :
                            <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "auto", maxHeight: "60vh" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: "12px", color: "#888", position: "sticky", top: 0, zIndex: 1, minWidth: "1400px" }}>
                                    {cell(40, "보냄")}{cell(70, "이름")}{cell(70, "지점")}{cell(140, "파트")}{cell(170, "탬플릿")}{cell(330, "계약기간 (자동)")}{cell(90, "시급")}{cell(110, "소정근로시간")}{cell(70, "변수 상세")}{cell("auto", "상태", { flex: 1 })}
                                </div>
                                {rows.map((r) => (
                                    <div key={r.positionId} style={{ borderBottom: "1px solid #f0f0f0", background: r.ok ? "white" : "#fafafa", minWidth: "1400px" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px", fontSize: "13px", fontFamily: "Apple_R" }}>
                                            {cell(40, <Checkbox size="sm" checked={r.send} disabled={!r.ok} onChange={(e) => updateRow(r.positionId, { send: e.target.checked })} />)}
                                            {cell(70, <span style={{ fontFamily: "Apple_SB" }}>{r.name}</span>)}
                                            {cell(70, r.branchLabel)}
                                            {cell(140, <span title={r.partName} style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.partName || "-"}</span>)}
                                            {cell(170, <span title={r.templateName} style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: r.templateName ? "#111" : "#9ca3af" }}>{r.templateName || "(없음)"}</span>)}
                                            {cell(330, (
                                                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                                    <Input size="sm" type="date" value={r.period.start} disabled={!r.ok} sx={{ ...inputSx(isPeriodEdited(r)), width: "155px" }} onChange={(e) => updateRow(r.positionId, { period: { ...r.period, start: e.target.value } })} />
                                                    <span>~</span>
                                                    <Input size="sm" type="date" value={r.period.end} disabled={!r.ok} sx={{ ...inputSx(isPeriodEdited(r)), width: "155px" }} onChange={(e) => updateRow(r.positionId, { period: { ...r.period, end: e.target.value } })} />
                                                </div>
                                            ))}
                                            {cell(90, <Input size="sm" value={r.values["common.hourlyWage"] || ""} disabled={!r.ok} sx={inputSx(isEdited(r, "common.hourlyWage"))} onChange={(e) => updateValue(r.positionId, "common.hourlyWage", e.target.value)} />)}
                                            {cell(110, <Input size="sm" value={r.values["part.weeklyHours"] || ""} disabled={!r.ok} sx={inputSx(isEdited(r, "part.weeklyHours"))} onChange={(e) => updateValue(r.positionId, "part.weeklyHours", e.target.value)} />)}
                                            {cell(70, <Button size="sm" variant="outlined" color="neutral" sx={{ whiteSpace: "nowrap" }} onClick={() => updateRow(r.positionId, { expanded: !r.expanded })}>{r.expanded ? "접기" : "펼치기"}</Button>)}
                                            {cell("auto", renderStatus(r), { flex: 1, minWidth: 0 })}
                                        </div>

                                        {r.expanded &&
                                            <div style={{ padding: "4px 8px 12px 58px", background: "#fcfcfd" }}>
                                                {CONTRACT_VAR_KEYS.filter((k) => !INLINE_KEYS.includes(k.value) && r.values[k.value] !== undefined).map((k) => {
                                                    const readOnly = readOnlyKeys.includes(k.value) || !editableKeys.includes(k.value);
                                                    return (
                                                        <div key={k.value}>
                                                            <div className={styles.fieldLabel} style={{ margin: "8px 0 2px" }}>{varKeyLabel(k.value)}{readOnly ? " (읽기 전용)" : ""}</div>
                                                            <Input size="sm" value={r.values[k.value] || ""} disabled={!r.ok || readOnly} sx={inputSx(isEdited(r, k.value))} onChange={(e) => updateValue(r.positionId, k.value, e.target.value)} />
                                                        </div>
                                                    );
                                                })}
                                                <div className={styles.subText} style={{ marginTop: "8px" }}>
                                                    여기서 고친 값은 이 직원·이 파트의 개인 변수로 저장됨 (명부 "변수 확인"과 같은 값, 다음 달에도 유지) · 회사 기본값은 "계약서 변수 관리"에서
                                                </div>
                                            </div>
                                        }
                                    </div>
                                ))}
                                {rows.length === 0 && !loading && <div style={{ padding: "20px", color: "#9ca3af", fontFamily: "Apple_R" }}>선택된 직무가 없습니다</div>}
                            </div>
                        }

                        <div className={styles.modalButtons}>
                            <div className={styles.hint}>발송되면 유캔싸인에 문서가 만들어지고 직원에게 카톡이 감 · 서명하면 명부 상태가 자동으로 "서명완료"로 바뀜</div>
                            <div style={{ display: "flex", gap: "8px" }}>
                                <Button variant="plain" color="neutral" onClick={() => props.onClose(false)} disabled={sending}>취소</Button>
                                {progress && <span className={styles.subText} style={{ alignSelf: "center" }}>{progress.done}/{progress.total}건 처리 중 · 창을 닫지 마세요</span>}
                                <Button variant="solid" onClick={send} loading={sending} disabled={targets.length === 0 || loading}>{targets.length}건 발송하기</Button>
                            </div>
                        </div>
                    </>
                }

                {results !== null &&
                    <>
                        <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden" }}>
                            <div style={{ display: "flex", gap: "10px", padding: "8px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: "12px", color: "#888" }}>
                                {cell(100, "이름")}{cell(160, "파트")}{cell(120, "결과")}{cell("auto", "내용", { flex: 1 })}
                            </div>
                            {results.map((res) => {
                                const r = rows.find((x) => x.positionId === res.positionId);
                                return (
                                    <div key={res.positionId} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px", borderBottom: "1px solid #f0f0f0", fontSize: "13px", fontFamily: "Apple_R" }}>
                                        {cell(100, <span style={{ fontFamily: "Apple_SB" }}>{r ? r.name : res.positionId}</span>)}
                                        {cell(160, r ? r.partName : "")}
                                        {cell(120, res.ok ? <Chip size="sm" variant="soft" color="success">발송됨</Chip> : res.unknown ? <Chip size="sm" variant="soft" color="warning">확인 필요</Chip> : res.skipped ? <Chip size="sm" variant="soft" color="neutral">보내지 않음</Chip> : <Chip size="sm" variant="soft" color="danger">실패</Chip>)}
                                        {cell("auto", (
                                            <span className={styles.subText} style={{ fontSize: "12px" }}>
                                                {res.ok ? res.documentName : res.error}
                                                {res.videoConsent ? (res.videoConsent.ok ? " · 영상동의서 함께 발송" : ` · 영상동의서 실패: ${res.videoConsent.error}`) : ""}
                                            </span>
                                        ), { flex: 1 })}
                                    </div>
                                );
                            })}
                        </div>
                        <div className={styles.modalButtons}>
                            <div className={styles.hint}>
                                성공 {results.filter((r) => r.ok).length}건 · 실패 {results.filter((r) => !r.ok && !r.unknown && !r.skipped).length}건 (실패 건은 기록이 남지 않음, 사유 확인 후 다시 발송)
                                {results.some((r) => r.unknown) && ` · 확인 필요 ${results.filter((r) => r.unknown).length}건 (명부에서 발송 여부 확인 후 안 간 사람만 다시)`}
                                {results.some((r) => r.skipped) && ` · 보내지 않음 ${results.filter((r) => r.skipped).length}건`}
                            </div>
                            <Button variant="solid" onClick={() => props.onClose(true)}>닫기</Button>
                        </div>
                    </>
                }

            </ModalDialog>
        </Modal>
    );
};

export default SendContractModal;
